/* global RequestInit */
interface IError {
    message: string;
    responseJSON?: string;
}

export default {
    sendRequest<T>(url: string, init?: RequestInit): Promise<T> {
        return fetch(url, init).then((resp) => {
            if (resp.ok) {
                return resp.json();
            } else {
                throw new Error(`Error while fetching ${url}, causee ${resp}`);
            }
        });
    },

    send(settings: any): Promise<any> {
        return fetch(settings.url, {
            method: settings.method || 'GET',
            headers: settings.headers,
            body: prepareBody(settings),
        })
            .then((response) => {
                if (response.status === 401) {
                    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
                    document.cookie = 'login=; ' + expire;
                    document.cookie = 'customer=; ' + expire;
                    document.cookie = 'XTrack-Web-Token=; ' + expire;
                    throw { status: response.status };
                } else if (!response.ok) {
                    return response.text().then((text) => {
                        let error: IError = { message: text };
                        try {
                            error.responseJSON = JSON.parse(text);
                        } catch (e) {
                            // If response body is not valid JSON, ignore parsing error
                        }
                        throw {
                            status: response.status,
                            responseJSON: error.responseJSON,
                        };
                    });
                }
                return prepareResponse(settings, response);
            })
            .catch((error) => {
                console.error('Fetch error:', error);
                throw error;
            });
    },
    getJsonConfiguration(fileName: string): Promise<any> {
        return fetch('/configuration/' + fileName)
            .then((response) => response.json())
            .catch((error) => {
                console.error('Fetch error:', error);
                throw error;
            });
    },
    sendWithoutErrorsHandling(settings: any): Promise<any> {
        return fetch(settings.url, {
            method: settings.method || 'GET',
            headers: settings.headers,
            body: prepareBody(settings),
        })
            .then((response) => {
                if (response.status === 401) {
                    const expire = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';
                    document.cookie = 'login=; ' + expire;
                    document.cookie = 'customer=; ' + expire;
                    document.cookie = 'XTrack-Web-Token=; ' + expire;
                    throw { status: response.status };
                }
                return prepareResponse(settings, response);
            })
            .catch((error) => {
                console.error('Fetch error:', error);
                throw error;
            });
    },
    async downloadFile(settings: any, filename?: string): Promise<void> {
        const headers = { ...settings.headers };
        delete headers['Accept'];

        try {
            const response = await fetch(settings.url, {
                method: settings.method || 'GET',
                headers,
                credentials: settings.withCredentials
                    ? 'include'
                    : 'same-origin',
            });

            if (!response.ok) {
                throw new Error(
                    `Failed to download file from ${settings.url} (status ${response.status})`
                );
            }

            const contentDisposition = response.headers.get(
                'Content-Disposition'
            );
            let suggestedFilename = filename;

            //get filename
            if (contentDisposition?.includes('filename=')) {
                const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
                    contentDisposition
                );
                if (match?.[1]) {
                    suggestedFilename = match[1].replace(/['"]/g, '');
                }
            }

            //simulate clicking on download link
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            if (suggestedFilename) {
                a.download = suggestedFilename;
            }
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('File download error:', error);
            throw error;
        }
    },
    async sendWithRetry(
        settings: any,
        maxRetries = 3,
        delay = 1000,
        retryStatus = 202
    ) {
        const response = await fetch(settings.url, {
            method: settings.method || 'GET',
            headers: settings.headers,
            body: prepareBody(settings),
        });
        if (response.status === retryStatus) {
            const result = await retryFetch(
                settings,
                maxRetries,
                delay,
                retryStatus
            );
            return prepareResponse(settings, result);
        } else {
            return prepareResponse(settings, response);
        }
    },
};

const retryFetch = async (
    settings: any,
    retriesLeft = 3,
    delay = 1000,
    retryStatus = 202
): Promise<Response> => {
    if (retriesLeft === 0) {
        throw new Error('Maximum number of retries reached');
    }

    await new Promise((resolve) => setTimeout(resolve, delay)); // Adjust delay as needed

    const response = await fetch(settings.url, {
        method: settings.method || 'GET',
        headers: settings.headers,
        body: prepareBody(settings),
    });

    if (response.status === retryStatus) {
        return retryFetch(settings, retriesLeft - 1, delay, retryStatus);
    } else {
        return response;
    }
};

const prepareResponse = (settings: any, response: Response) => {
    if (settings.method === 'HEAD') {
        return null;
    }
    if (settings.method === 'DELETE') {
        return response;
    }
    const contentType = response.headers.get('content-type');
    if (contentType) {
        return response.json();
    }
    return response;
};

const prepareBody = (settings: any) => {
    if (
        !settings ||
        !settings.method ||
        settings.method === 'GET' ||
        settings.method === 'HEAD'
    ) {
        return undefined;
    }
    if (
        settings.contentType ===
        'application/x-www-form-urlencoded; charset=UTF-8'
    ) {
        return new URLSearchParams(settings.data);
    }
    if (settings.contentType === 'application/json') {
        return settings.data;
    }
    return settings.data;
};
